import { useState } from 'react';
import type { ReactNode } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { ErrorAlert } from '../../components/ErrorAlert';
import { useEditMachineVersion, useMachineVersions } from '../../api/queries';
import { useAlert, useConfirm } from '../../components/dialogs/DialogProvider';
import { fmtDateTime, fmtOrEmDash } from '../../utils/format';
import type { MachineVersion } from '../../api/types';

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '180px 1fr', columnGap: 2, rowGap: 0.5, mb: 0.5 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" component="div">{value}</Typography>
    </Box>
  );
}

/** Basis points (1/100 of a percent) → a "%" display, e.g. 9500 → "95.00%". */
export function fmtBp(bp: number | null): string {
  return bp != null ? `${(bp / 100).toFixed(2)}%` : '—';
}

type ParsedIntField = { ok: true; value: number | null } | { ok: false };

/**
 * Parses one of the version-edit box's numeric text fields. Blank means
 * "leave unchanged" → `null` (the API keeps the current value on a `null`
 * field — see `MachineVersionService.EditActiveVersionAsync`). Anything
 * else must parse to a finite integer within `[min, max]` or the field is
 * rejected outright: `Number('not a number')` is `NaN`, and
 * `JSON.stringify(NaN)` silently serializes to `null` — which the API reads
 * as "no change" and returns 204, making a typo look like a successful save
 * when nothing actually changed.
 */
function parseIntField(raw: string, min: number, max: number): ParsedIntField {
  const trimmed = raw.trim();
  if (trimmed === '') return { ok: true, value: null };
  const n = Number(trimmed);
  if (!Number.isInteger(n) || n < min || n > max) return { ok: false };
  return { ok: true, value: n };
}

export type MachineVersionsTabProps = {
  machineId: string;
  canEdit: boolean;
};

/**
 * Game-version history (financial meters spec §6.1/§9.4/§5.2) — one row per
 * closed period of stable game/paytable/denomination identity for this
 * machine. The active row (no `endedAt`) is the one currently accruing
 * meters; a version the meters engine opened automatically for review
 * (`needsReview`, e.g. an EGM swap or a game-config mismatch) surfaces a
 * loud warning chip until a human confirms the details are correct.
 *
 * `canEdit` is decided by the caller from useMyPermissions
 * (`machines.edit`) so the edit box is hidden — not merely disabled — for
 * non-holders (PORTAL_FORM_RULES §11/§12), same pattern as
 * FirmwareSkewPanel's `canPromote`.
 */
// History view is read-only; editing/confirming the active version lives on
// the Configuration tab (ActiveVersionConfigPanel). machineId is the only
// input this view needs.
export function MachineVersionsTab({ machineId }: { machineId: string }) {
  const versions = useMachineVersions(machineId);

  if (versions.isLoading) {
    return (
      <Stack direction="row" spacing={2} alignItems="center">
        <CircularProgress size={20} /><Typography>Loading…</Typography>
      </Stack>
    );
  }
  if (versions.error) {
    return <ErrorAlert>{(versions.error as { message?: string }).message}</ErrorAlert>;
  }

  const rows = versions.data ?? [];
  if (rows.length === 0) {
    return <Typography color="text.secondary">No versions yet.</Typography>;
  }

  return (
    <Stack spacing={2}>
      <TableContainer>
        <Table size="small" aria-label="game-version history">
          <TableHead>
            <TableRow>
              <TableCell>Version</TableCell>
              <TableCell>Game</TableCell>
              <TableCell>RTP</TableCell>
              <TableCell>Denom</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>Started</TableCell>
              <TableCell>Ended</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((v) => (
              <TableRow key={v.id} hover data-testid={`version-row-${v.versionNo}`}>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <span>{`v${v.versionNo}`}</span>
                    {v.endedAt === null && <Chip size="small" label="active" color="success" />}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <span>{fmtOrEmDash(v.gameName)}</span>
                    {v.needsReview && <Chip size="small" label="check details" color="warning" />}
                  </Stack>
                </TableCell>
                <TableCell>{fmtBp(v.rtpBasisPoints)}</TableCell>
                <TableCell>{`${v.denominationCents}¢`}</TableCell>
                <TableCell>{v.reason}</TableCell>
                <TableCell>{fmtDateTime(v.startedAt)}</TableCell>
                <TableCell>{fmtDateTime(v.endedAt)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="caption" color="text.secondary">
        Game name, RTP and denomination are per-version — edit and confirm them on the Configuration tab.
      </Typography>
    </Stack>
  );
}

/**
 * The ACTIVE game version's configuration (game name, IDs, paytable, RTP,
 * denomination, wire base %, EGM serial), shown by version number — this is
 * the machine's live game identity per the financial-meters model. When
 * `canEdit`, the inline editor + "Confirm details" (clears the review flag)
 * live here. Used by the machine detail dialog's Configuration tab.
 */
export function ActiveVersionConfigPanel({ machineId, canEdit }: MachineVersionsTabProps) {
  const versions = useMachineVersions(machineId);

  if (versions.isLoading) {
    return (
      <Stack direction="row" spacing={2} alignItems="center">
        <CircularProgress size={20} /><Typography>Loading…</Typography>
      </Stack>
    );
  }
  if (versions.error) {
    return <ErrorAlert>{(versions.error as { message?: string }).message}</ErrorAlert>;
  }

  const active = (versions.data ?? []).find((v) => v.endedAt === null) ?? null;
  if (!active) {
    return <Typography color="text.secondary">No active game version yet — configuration appears once the machine reports.</Typography>;
  }

  return (
    <Stack spacing={1.5}>
      <Row
        label="Active version"
        value={
          <Stack direction="row" spacing={1} alignItems="center">
            <span>v{active.versionNo}</span>
            {active.needsReview && <Chip size="small" label="check details" color="warning" />}
          </Stack>
        }
      />
      <Row label="Game name"    value={fmtOrEmDash(active.gameName)} />
      <Row label="Game ID"      value={active.gameId
        ? <>{active.gameId}{active.additionalGameId ? ` (+${active.additionalGameId})` : ''}</>
        : fmtOrEmDash(null)} />
      <Row label="Paytable"     value={fmtOrEmDash(active.paytableId)} />
      <Row label="RTP"          value={fmtBp(active.rtpBasisPoints)} />
      <Row label="Denomination" value={`${active.denominationCents}¢`} />
      <Row label="Wire base %"  value={fmtBp(active.wireBasePctBp)} />
      <Row label="Max bet"      value={fmtOrEmDash(active.maxBet)} />
      <Row label="EGM serial"   value={fmtOrEmDash(active.egmSerialObserved)} />

      {canEdit && (
        <>
          <Divider sx={{ my: 1 }} />
          <ActiveVersionEditor key={active.id} machineId={machineId} version={active} />
        </>
      )}
    </Stack>
  );
}

/**
 * Inline edit box for the currently-active version, plus (when
 * `needsReview`) the "Confirm details" action that clears the review flag.
 * Keyed by `version.id` from the parent so the local field state resets
 * cleanly whenever the active version changes underneath it (a save,
 * confirm, or a fresh meters-engine version cut).
 */
export function ActiveVersionEditor({ machineId, version }: { machineId: string; version: MachineVersion }) {
  const edit = useEditMachineVersion(machineId);
  const confirm = useConfirm();
  const alert = useAlert();

  const [gameName, setGameName] = useState(version.gameName ?? '');
  const [rtp, setRtp] = useState(version.rtpBasisPoints != null ? String(version.rtpBasisPoints) : '');
  const [denom, setDenom] = useState(String(version.denominationCents));

  const onSaveError = (title: string) => (e: unknown) => {
    alert({
      title,
      message: (e as { message?: string })?.message ?? 'The request did not complete.',
      severity: 'error',
    });
  };

  const onSave = () => {
    // Validate BEFORE mutating — an unparsable/out-of-range field must never
    // silently coerce to `null` (see parseIntField) and look like a no-op
    // success. Blank fields are the one legitimate "leave unchanged" case.
    const rtpField = parseIntField(rtp, 0, 20000);
    if (!rtpField.ok) {
      alert({
        title: 'Invalid RTP',
        message: 'Enter a whole number of basis points from 0 to 20000 (e.g. 9500 for 95.00%), or leave the field blank to keep the current value.',
        severity: 'error',
      });
      return;
    }
    const denomField = parseIntField(denom, 1, Number.MAX_SAFE_INTEGER);
    if (!denomField.ok) {
      alert({
        title: 'Invalid denomination',
        message: 'Enter a positive whole number of cents, or leave the field blank to keep the current value.',
        severity: 'error',
      });
      return;
    }
    edit.mutate(
      {
        versionId: version.id,
        gameName: gameName.trim() || null,
        rtpBasisPoints: rtpField.value,
        denominationCents: denomField.value,
      },
      {
        onSuccess: () => alert({
          title: 'Saved', message: `Version v${version.versionNo} updated.`, severity: 'success',
        }),
        onError: onSaveError('Save failed'),
      },
    );
  };

  const onConfirmDetails = async () => {
    const ok = await confirm({
      title: 'Confirm game details',
      message: `Confirm game details for v${version.versionNo}? This clears the review flag.`,
      confirmLabel: 'Confirm details',
    });
    if (!ok) return;
    edit.mutate(
      { versionId: version.id, confirmReview: true },
      {
        onSuccess: () => alert({
          title: 'Confirmed', message: `Version v${version.versionNo} confirmed.`, severity: 'success',
        }),
        onError: onSaveError('Confirm failed'),
      },
    );
  };

  return (
    <Box data-testid="version-edit-box" sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="overline" color="text.secondary">
          Edit v{version.versionNo}
        </Typography>
        <Stack direction="row" spacing={2}>
          <TextField
            label="Game name"
            size="small"
            value={gameName}
            onChange={(e) => setGameName(e.target.value)}
          />
          <TextField
            label="RTP (bp)"
            size="small"
            value={rtp}
            onChange={(e) => setRtp(e.target.value)}
          />
          <TextField
            label="Denomination (¢)"
            size="small"
            value={denom}
            onChange={(e) => setDenom(e.target.value)}
          />
        </Stack>
        {edit.error && (
          <ErrorAlert>{(edit.error as { message?: string }).message}</ErrorAlert>
        )}
        <Stack direction="row" spacing={1}>
          <Button variant="contained" size="small" onClick={onSave} disabled={edit.isPending}>
            {edit.isPending ? 'Saving…' : 'Save'}
          </Button>
          {version.needsReview && (
            <Button
              variant="outlined"
              color="warning"
              size="small"
              onClick={() => void onConfirmDetails()}
              disabled={edit.isPending}
              data-testid="version-confirm-details"
            >
              Confirm details
            </Button>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}
